import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Briefcase, Code, Megaphone, Zap, Heart, TrendingUp, Users, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../utils/cn';
import API_URL from '../config';
import Button from '../components/ui/Button';

const jobRoles = [
  {
    title: 'Software Development Engineer',
    icon: <Code className="w-6 h-6 text-brand-600 dark:text-brand-400" />,
    description: 'Responsible for end-to-end frontend and backend development of our core products including microservices and web interfaces.',
    skills: ['React / Next.js', 'Python / Node.js', 'PostgreSQL / Vector DB', 'Docker / CI/CD', 'REST & WebSockets']
  },
  {
    title: 'AI & Data Science Contributor',
    icon: <Briefcase className="w-6 h-6 text-indigoBrand-600 dark:text-indigoBrand-400" />,
    description: 'Work on retrieval-augmented generation (RAG) pipelines, computer vision models, and deepfake forensic detection algorithms.',
    skills: ['PyTorch / TensorFlow', 'NLP & Embeddings', 'FAISS / Chroma', 'OpenCV / Signal Processing', 'Model Optimization']
  },
  {
    title: 'Technical Growth & Product Outreach',
    icon: <Megaphone className="w-6 h-6 text-amber-600 dark:text-amber-400" />,
    description: 'Lead technical product documentation, developer community outreach, university liaison, and enterprise growth strategies.',
    skills: ['Technical Writing', 'Developer Relations', 'Growth Analytics', 'B2B Communication', 'Product Documentation']
  }
];

const benefits = [
  { icon: <Zap className="w-5 h-5 text-brand-600" />, title: 'Practical Learning', desc: 'Work directly on production-targeted codebases and real problems.' },
  { icon: <Heart className="w-5 h-5 text-indigoBrand-600" />, title: 'Engineering Culture', desc: 'A transparent, collaborative environment that values code quality.' },
  { icon: <TrendingUp className="w-5 h-5 text-emerald-600" />, title: 'Meaningful Impact', desc: 'Build technology in healthcare, cybersecurity, and intelligent mobility.' },
  { icon: <Users className="w-5 h-5 text-amber-600" />, title: 'Experienced Mentorship', desc: 'Receive direct architectural feedback and code review guidance.' },
];

const Hiring = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    full_name: '',
    mobile_number: '',
    email: '',
    resume_link: '',
    position: 'Software Development Engineer',
    skills: '',
    introduction: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      const res = await fetch(`${API_URL}/api/hiring/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setSubmitStatus({ type: 'success', message: data.message || 'Application received successfully! Our team will review your profile.' });
        setFormData({
          full_name: '', mobile_number: '', email: '', resume_link: '',
          position: 'Software Development Engineer', skills: '', introduction: ''
        });
      } else {
        setSubmitStatus({ type: 'error', message: data.error || 'Submission failed. Please try again.' });
      }
    } catch (error) {
      setSubmitStatus({ type: 'error', message: 'Failed to connect to the application server. Please try again.' });
    }
    setIsSubmitting(false);
  };

  return (
    <div className="pt-28 pb-20 min-h-screen bg-slate-50/50 dark:bg-slate-950">
      <div className="container mx-auto px-6 md:px-12">
        
        {/* Navigation back */}
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-brand-600 transition-colors mb-8 group font-medium text-sm"
        >
          <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
          <span>Back to Homepage</span>
        </button>

        {/* Hero Section */}
        <section className="max-w-3xl mx-auto text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 text-xs font-semibold uppercase tracking-wider mb-4">
              Career Opportunities
            </div>
            <h1 className="text-3xl md:text-5xl font-display font-bold mb-5 text-slate-900 dark:text-white tracking-tight">
              Join <span className="text-gradient">Mira Future Tech Vision</span>
            </h1>
            <p className="text-slate-600 dark:text-slate-300 text-lg leading-relaxed">
              We are looking for motivated engineers, researchers, and creators to contribute to our mission-critical technology platforms.
            </p>
          </motion.div>
        </section>

        {/* Why Join Us */}
        <section className="mb-20">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-2">Why Build With Us?</h2>
            <p className="text-sm text-slate-500">How we collaborate and support engineering talent.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {benefits.map((benefit, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.08 }}
                className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200/90 dark:border-slate-800 shadow-subtle"
              >
                <div className="w-10 h-10 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200/60 dark:border-slate-700 flex items-center justify-center mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white mb-1.5">{benefit.title}</h3>
                <p className="text-slate-600 dark:text-slate-400 text-xs leading-relaxed">{benefit.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Open Roles */}
        <section className="mb-20">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-2">Open Positions</h2>
            <p className="text-sm text-slate-500">Current active roles open for application.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {jobRoles.map((role, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.08 }}
                className="bg-white dark:bg-slate-900 p-7 rounded-2xl border border-slate-200/90 dark:border-slate-800 shadow-subtle flex flex-col justify-between"
              >
                <div>
                  <div className="w-12 h-12 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200/60 dark:border-slate-700 flex items-center justify-center mb-5">
                    {role.icon}
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">{role.title}</h3>
                  <p className="text-slate-600 dark:text-slate-400 text-xs leading-relaxed mb-5">{role.description}</p>
                </div>

                <div>
                  <h4 className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Relevant Competencies</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {role.skills.map((skill, i) => (
                      <span key={i} className="px-2 py-0.5 rounded text-[11px] bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Application Form */}
        <section className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white dark:bg-slate-900 rounded-3xl p-8 md:p-12 border border-slate-200 dark:border-slate-800 shadow-card"
          >
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-display font-bold text-slate-900 dark:text-white mb-2">
                Submit Your Application
              </h2>
              <p className="text-slate-500 text-sm">Provide your information below and our team will get in touch.</p>
            </div>

            {submitStatus && (
              <div className={cn(
                "p-4 rounded-xl mb-6 text-center text-xs font-semibold",
                submitStatus.type === 'success' 
                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200" 
                  : "bg-red-50 text-red-700 border border-red-200"
              )}>
                {submitStatus.message}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Full Name *</label>
                  <input 
                    required 
                    type="text" 
                    name="full_name" 
                    value={formData.full_name} 
                    onChange={handleChange}
                    className="w-full bg-slate-50/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all"
                    placeholder="John Doe" 
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Mobile Number *</label>
                  <input 
                    required 
                    type="tel" 
                    name="mobile_number" 
                    value={formData.mobile_number} 
                    onChange={handleChange}
                    className="w-full bg-slate-50/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all"
                    placeholder="+91 9876543210" 
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Email Address *</label>
                  <input 
                    required 
                    type="email" 
                    name="email" 
                    value={formData.email} 
                    onChange={handleChange}
                    className="w-full bg-slate-50/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all"
                    placeholder="john@example.com" 
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Position Applying For *</label>
                  <select 
                    required 
                    name="position" 
                    value={formData.position} 
                    onChange={handleChange}
                    className="w-full bg-slate-50/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all appearance-none"
                  >
                    <option value="Software Development Engineer">Software Development Engineer</option>
                    <option value="AI & Data Science Contributor">AI & Data Science Contributor</option>
                    <option value="Technical Growth & Product Outreach">Technical Growth & Product Outreach</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Resume / Portfolio Link (Google Drive, LinkedIn, GitHub) *</label>
                <input 
                  required 
                  type="url" 
                  name="resume_link" 
                  value={formData.resume_link} 
                  onChange={handleChange}
                  className="w-full bg-slate-50/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all"
                  placeholder="https://drive.google.com/... or https://linkedin.com/in/..." 
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Key Skills & Experience *</label>
                <textarea 
                  required 
                  name="skills" 
                  value={formData.skills} 
                  onChange={handleChange} 
                  rows="3"
                  className="w-full bg-slate-50/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all resize-none"
                  placeholder="List relevant languages, frameworks, and projects you have built..."
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Brief Introduction *</label>
                <textarea 
                  required 
                  name="introduction" 
                  value={formData.introduction} 
                  onChange={handleChange} 
                  rows="3"
                  className="w-full bg-slate-50/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all resize-none"
                  placeholder="Tell us what excites you about building technology with Mira Future Tech Vision..."
                />
              </div>

              <div className="pt-2 text-center">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  variant="primary"
                  className="px-8 py-3.5 font-semibold shadow-xs min-w-[200px]"
                >
                  {isSubmitting ? 'Submitting Application...' : 'Submit Application'}
                </Button>
              </div>
            </form>
          </motion.div>
        </section>

      </div>
    </div>
  );
};

export default Hiring;
